import React, { Fragment, useState, useEffect, FC, useCallback, useMemo } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import ClearDataButton from "components/HeroSearchForm/ClearDataButton";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import { useBooking } from "context/BookingContext";
import { parseISO, isSameDay, format as formatDate } from "date-fns";
import useDailyRates from "hooks/useDailyRates";
import { useClient } from "ClientContext";
import { useTranslation } from "react-i18next";
import BookingRates from "components/calendar/BookingRates";

export interface StayDatesRangeInputProps {
  className?: string;
  onDatesChange?: () => void;
  dates: string[];
}

const TODAY = new Date();

const DatesRangeInputWQuote: FC<StayDatesRangeInputProps> = ({
  className = "flex-1",
  onDatesChange,
  dates,
}) => {
  const { t } = useTranslation();
  const { integration_TYPE } = useClient();
  const { bookingState, setBookingState } = useBooking();
  const { searchParams, listingId, roomId } = bookingState;

  const { ratesCalendar, ratesLoading } = useDailyRates({ listingId, roomId: roomId || "" });
  
  const [startDate, setStartDate] = useState<Date | null>(searchParams?.checkIn || null);
  const [endDate, setEndDate] = useState<Date | null>(searchParams?.checkOut || null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // keep start/end in sync with booking context
  useEffect(() => {
    const { checkIn, checkOut } = searchParams || {};
    setStartDate(checkIn || null);
    setEndDate(checkOut || null);
  }, [searchParams]);

  // Memoize prices array to prevent unnecessary re-renders
  const prices = useMemo(() => ratesCalendar?.dates ?? [], [ratesCalendar?.dates]);

  // Create price lookup map for O(1) price lookups instead of O(n) find operations
  const priceMap = useMemo(() => {
    const map = new Map<string, any>();
    prices.forEach((priceObj: any) => {
      if (priceObj.date) {
        map.set(priceObj.date, priceObj);
      }
    });
    return map;
  }, [prices]);

  // Optimize date normalization - remove logging and simplify
  const allLocalMidnights: Date[] = useMemo(() => {
    return dates.map(dateStr => {
      const utcDate = parseISO(dateStr);
      return new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate()
      );
    });
  }, [dates]);

  // Create a Set for O(1) disabled date lookups
  const disabledDatesSet = useMemo(() => {
    const set = new Set<string>();
    allLocalMidnights.forEach(date => {
      const dateKey = formatDate(date, "yyyy-MM-dd");
      set.add(dateKey);
    });
    return set;
  }, [allLocalMidnights]);

  // Optimize disabled dates computation
  const disabledDates: Date[] = useMemo(() => {
    if (!startDate) {
      return allLocalMidnights;
    }

    // Find the next disabled date after startDate more efficiently
    const startDateStr = formatDate(startDate, "yyyy-MM-dd");
    const sortedDates = allLocalMidnights
      .filter(date => date > startDate)
      .sort((a, b) => a.getTime() - b.getTime());
    
    const nextDisabledDate = sortedDates[0];

    return allLocalMidnights.filter(date => {
      // Enable check-in date
      if (isSameDay(date, startDate)) {
        return false;
      }
      
      // Enable the next disabled date after check-in
      if (nextDisabledDate && isSameDay(date, nextDisabledDate)) {
        return false;
      }

      return true;
    });
  }, [allLocalMidnights, startDate]);

  // Memoize the date change handler
  const onChangeDate = useCallback(
    (datesPair: [Date | null, Date | null]) => {
      const [start, end] = datesPair;
      setStartDate(start);
      setEndDate(end);
      setBookingState(prev => ({
        ...prev,
        searchParams: { ...prev.searchParams, checkIn: start, checkOut: end },
      }));
      onDatesChange?.();
    },
    [onDatesChange, setBookingState]
  );

  // Memoize the input renderer
  const renderInput = useCallback(() => (
    <div className="flex-grow text-left">
      <span className="block xl:text-lg font-semibold">
        {startDate
          ? startDate.toLocaleDateString("en-US", { month: "short", day: "2-digit" })
          : t("Add dates")}
        {endDate
          ? ` - ${endDate.toLocaleDateString("en-US", { month: "short", day: "2-digit" })}`
          : ""}
      </span>
      <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
        {`${t("Check in")} - ${t("Check out")}`}
      </span>
    </div>
  ), [startDate, endDate, t]);

  // Optimize day rendering with better memoization and O(1) lookups
  const renderDayContents = useCallback((day: number, date?: Date) => {
    if (!date) return day;
    
    const dateString = formatDate(date, "yyyy-MM-dd");
    const isDisabled = disabledDates.some(d => isSameDay(d, date));
    const priceObj = isDisabled ? null : priceMap.get(dateString);
    const price = priceObj?.price || null;
    
    return (
      <BookingRates
        day={day}
        date={date}
        price={price}
        isDisabled={isDisabled}
        isLoading={ratesLoading}
      />
    );
  }, [disabledDates, priceMap, ratesLoading]);

  // Memoize custom header to prevent unnecessary re-renders
  const renderCustomHeader = useCallback(
    (props: any) => <DatePickerCustomHeaderTwoMonth {...props} />,
    []
  );

  // Early return for loading/error states
  if (loading || !integration_TYPE || error) {
    return loading ? <div>Loading...</div> : <div>Error: {error}</div>;
  }

  return (
    <Popover className={`StayDatesRangeInput z-10 relative flex ${className}`}>
      {({ open, close }) => (
        <>
          <Popover.Button
            className={`flex-1 flex relative p-3 items-center space-x-3 focus:outline-none ${
              open ? "shadow-lg" : ""
            }`}>
            <div className="text-neutral-300 dark:text-neutral-400">
              <CalendarIcon className="w-5 h-5 lg:w-7 lg:h-7" />
            </div>
            {renderInput()}
            {startDate && open && <ClearDataButton onClick={() => onChangeDate([null, null])} />}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute left-auto xl:-right-10 right-0 z-10 mt-3 top-full w-screen max-w-sm px-4 sm:px-0 lg:max-w-3xl">
              <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-neutral-800 p-8">
                <DatePicker
                  minDate={TODAY}
                  selected={startDate}
                  onChange={(dates) => {
                    onChangeDate(dates);
                    // Close the popover when both dates are selected
                    const [start, end] = dates;
                    if (start && end) {
                      close();
                    }
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange
                  monthsShown={2}
                  showPopperArrow={false}
                  inline
                  excludeDates={disabledDates}
                  renderCustomHeader={renderCustomHeader}
                  renderDayContents={renderDayContents}
                />
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default DatesRangeInputWQuote;

